import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mic, Users, Lightbulb, Brain, Briefcase, GraduationCap } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPEAKING_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_6dc3909f-878c-4eee-8cfe-c2f27fad2cc4/artifacts/e01bc370d76a2411_5BEE6FA0-41B0-482A-9BCF-AF13083471D2.png";

export const topics = [
  { icon: Brain, title: "Adaptive Intelligence", desc: "How humans make better decisions in an AI-shaped world." },
  { icon: Briefcase, title: "The Future of Work Is Already Here", desc: "What leaders are still misunderstanding about AI-driven workforce transformation." },
  { icon: Lightbulb, title: "Design the Life First", desc: "Why career planning must change in an era of accelerating disruption." },
  { icon: GraduationCap, title: "AI, Education & the New Opportunity Divide", desc: "What schools, universities, and families need to understand now." },
  { icon: Users, title: "From AI to Human Agency", desc: "How organizations can deploy AI without losing human judgment." },
  { icon: Mic, title: "What Happens After the Entry-Level Job?", desc: "How AI may transform the traditional career ladder." },
];

const SpeakingPage = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", organization: "", eventType: "", eventDate: "", audienceSize: "", budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/speaking/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Thank you! I'll be in touch within 48 hours.");
        setFormData({ name: "", email: "", organization: "", eventType: "", eventDate: "", audienceSize: "", budget: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {/* Hero */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium">Speaking</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white italic leading-tight" data-testid="speaking-heading">
              Inspiring Action for an Adaptive Future
            </h1>
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
              Raulvin delivers keynote addresses, executive briefings, panels, and thought-provoking conversations about artificial intelligence, adaptive intelligence, the future of work, education, and economic opportunity.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#speaking-form" className="inline-flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-7 py-3.5 rounded-lg font-semibold transition-all" data-testid="speaking-invite-btn">
                Invite Raulvin to Speak <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/media/one-sheet" className="inline-flex items-center gap-2 border border-[rgba(124,58,237,0.4)] hover:border-[var(--accent-violet)] text-white px-7 py-3.5 rounded-lg font-medium transition-colors" data-testid="speaking-one-sheet-btn">
                Download Speaker One-Sheet
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-violet-600/15 to-transparent rounded-3xl blur-xl" />
              <img src={SPEAKING_IMG} alt="Raulvin Coke speaking" data-testid="speaking-hero-image" className="relative rounded-2xl w-full aspect-video object-cover shadow-2xl shadow-black/50" />
            </div>
          </motion.div>
        </div>

        {/* Topics */}
        <section className="mb-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-8">Keynote Topics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {topics.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="bg-[var(--bg-card)] border-glow rounded-xl p-6 card-hover"
              >
                <t.icon className="w-8 h-8 text-[var(--accent-violet-light)] mb-4" />
                <h3 className="font-display text-lg font-semibold text-white mb-2">{t.title}</h3>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Booking Form */}
        <section id="speaking-form" className="scroll-mt-24">
          <div className="bg-[var(--bg-card)] border-glow rounded-2xl p-8 md:p-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-3">Invite Raulvin to Speak</h2>
            <p className="text-[var(--text-secondary)] mb-8">Tell me about your event. Typical response time: within 48 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input type="text" placeholder="Your Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-name-input" />
                <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-email-input" />
              </div>
              <input type="text" placeholder="Organization *" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} required className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-org-input" />
              <div className="grid md:grid-cols-3 gap-4">
                <select value={formData.eventType} onChange={(e) => setFormData({ ...formData, eventType: e.target.value })} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-event-select">
                  <option value="">Event Type</option>
                  <option value="keynote">Keynote</option>
                  <option value="panel">Panel</option>
                  <option value="workshop">Workshop</option>
                  <option value="executive">Executive Briefing</option>
                  <option value="fireside">Fireside Chat</option>
                </select>
                <input type="date" value={formData.eventDate} onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-date-input" />
                <select value={formData.audienceSize} onChange={(e) => setFormData({ ...formData, audienceSize: e.target.value })} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none">
                  <option value="">Audience Size</option>
                  <option value="under50">Under 50</option>
                  <option value="50-200">50–200</option>
                  <option value="200-500">200–500</option>
                  <option value="500-1000">500–1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </div>
              <select value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none" data-testid="speaking-budget-select">
                <option value="">Speaking Budget</option>
                <option value="under5k">Under $5,000</option>
                <option value="5k-10k">$5,000 – $10,000</option>
                <option value="10k-25k">$10,000 – $25,000</option>
                <option value="25k-50k">$25,000 – $50,000</option>
                <option value="50k+">$50,000+</option>
              </select>
              <textarea placeholder="Tell me about your event, audience, and what you're hoping to achieve..." value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={4} className="w-full px-4 py-3 bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none resize-none" />
              <button type="submit" disabled={loading} className="w-full bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white py-4 rounded-lg font-semibold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="speaking-submit-btn">
                {loading ? "Sending..." : "Check Availability"}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SpeakingPage;
